// t() reads the page language, which the compiler cannot see, and this stays
// on screen while the language changes.
"use no memo";
import { useEffect, useEffectEvent } from "react";
import type { Person } from "../../shared/types";
import { number, t, useLocale } from "../i18n";
import {
  closeCallNotifications,
  closeMessageNotifications,
  closeGroupNotifications,
  pushSupported,
} from "./push";
import type { CallNotifications } from "./use-call-notifications";
import { ios, useInstalled } from "../pwa/install";
import { realtime, type ClientConnectionEvent } from "../data/realtime";
import { usePageVisible } from "../ui/use-page-visible";
import type { Call } from "../calls/call-state";
import { sounds } from "../sounds/sounds";

// Unread chats and incoming calls reach people whose tab is in the background.
export function useAttention(
  meId: string | undefined,
  friends: Person[],
  call: Call | null,
  pushOn: boolean,
  openChatId?: string,
) {
  const locale = useLocale();
  const visible = usePageVisible();
  const unread = friends.filter((friend) => friend.unread).length;
  const ringing = call?.status === "incoming" ? call : null;
  const ringingId = ringing?.id,
    callerName = ringing?.person.name,
    callId = call?.id;
  useEffect(() => {
    const title = t("رنّة | على الخط");
    document.title = callerName
      ? t("{name} يتصل بك…", { name: callerName })
      : unread
        ? `(${number(unread)}) ${title}`
        : title;
  }, [callerName, unread, locale]);
  // The server pushes call alerts to every subscribed device. A visible app
  // already shows the incoming call, so close its redundant system alert even
  // when delivery arrives after the signaling event or after an answer.
  useEffect(() => {
    if (!pushSupported) return;
    const close = () => void closeCallNotifications().catch(() => {});
    if (!ringingId || visible) close();
    const onShown = (event: MessageEvent) => {
      if (
        event.data?.type === "notification-shown" &&
        !document.hidden &&
        callId &&
        event.data.tag === `call:${callId}`
      )
        close();
    };
    navigator.serviceWorker.addEventListener("message", onShown);
    return () =>
      navigator.serviceWorker.removeEventListener("message", onShown);
  }, [ringingId, visible, callId]);
  // A visible app already shows new messages, so their alerts close at once.
  useEffect(() => {
    if (!pushSupported) return;
    const onShown = (event: MessageEvent) => {
      if (
        event.data?.type === "notification-shown" &&
        !document.hidden &&
        event.data.tag?.startsWith("message:")
      )
        void closeMessageNotifications().catch(() => {});
    };
    navigator.serviceWorker.addEventListener("message", onShown);
    return () =>
      navigator.serviceWorker.removeEventListener("message", onShown);
  }, []);
  // A conversation read here or on another device needs no alert anymore.
  const read = friends
    .filter((friend) => !friend.unread)
    .map((friend) => friend.id)
    .join();
  useEffect(() => {
    if (pushSupported)
      void closeMessageNotifications(read.split(",")).catch(() => {});
  }, [read]);
  // Message text doesn't stay on screen after leaving the account.
  useEffect(() => {
    if (!meId || !pushSupported) return;
    return () => {
      void closeMessageNotifications().catch(() => {});
      void closeGroupNotifications().catch(() => {});
    };
  }, [meId]);
  const onMessage = useEffectEvent((event: ClientConnectionEvent) => {
    if (
      event.type !== "message" ||
      event.message.recipient !== meId ||
      event.message.sender === meId
    )
      return;
    const friend = friends.find((person) => person.id === event.message.sender);
    if (!friend) return;
    // A hidden device that gets push alerts already hears the system's sound.
    if (!call && (document.hidden ? !pushOn : openChatId !== friend.id))
      sounds.message();
  });
  useEffect(() => {
    if (!meId) return;
    const seen = new Set<string>();
    return realtime.subscribe((event) => {
      if (event.type !== "message" || seen.has(event.message.id)) return;
      seen.add(event.message.id);
      if (seen.size > 100) seen.delete(seen.values().next().value!);
      onMessage(event);
    });
  }, [meId]);
}

export function NotificationSetting({
  notifications,
}: {
  notifications: CallNotifications;
}) {
  const {
    saved: push,
    savedFailed: failed,
    reloadSaved: retry,
  } = notifications;
  const installed = useInstalled();
  const { on, permission, busy, checking, error } = notifications;
  const installFirst = ios && !installed;
  const unavailable = !pushSupported || push?.publicKey === null;
  const waiting = !push && !failed && !installFirst && !unavailable;
  const blocked = permission === "denied";
  const description = installFirst
    ? "أضف رنّة إلى الشاشة الرئيسية وافتحه من الأيقونة لتفعيل الإشعارات. يتطلب iOS أو iPadOS 16.4 أو أحدث."
    : !pushSupported
      ? "الإشعارات غير مدعومة هنا. استخدم متصفحًا حديثًا أو تطبيق رنّة المثبّت."
      : failed
        ? "تعذر تحميل إعدادات الإشعارات. تحقق من اتصالك وحاول مجددًا."
        : unavailable
          ? "الإشعارات غير متاحة حاليًا"
          : waiting || checking
            ? "جارٍ التحقق من إشعارات هذا الجهاز…"
            : blocked
              ? ios
                ? "الإشعارات محظورة. اسمح بها لرنّة في إعدادات إشعارات الجهاز ثم افتحه مجددًا."
                : "الإشعارات محظورة في إعدادات المتصفح."
              : on
                ? "تصلك المكالمات والرسائل ودعوات المكالمات الجماعية على هذا الجهاز حتى عندما تكون رنّة مغلقة."
                : "نبّهني بالمكالمات والرسائل ودعوات المكالمات الجماعية حتى عندما تكون رنّة مغلقة.";
  return (
    <div className="setting-row">
      <div className="setting-text">
        <strong>{t("الإشعارات")}</strong>
        <small role="status">{t(description)}</small>
        {error && (
          <small className="form-error" role="alert">
            {t(error)}
          </small>
        )}
      </div>
      {failed && !installFirst ? (
        <button className="button secondary small" onClick={retry}>
          {t("إعادة المحاولة")}
        </button>
      ) : (
        !installFirst &&
        !unavailable &&
        !waiting &&
        (error || !blocked) && (
          <button
            type="button"
            className="button secondary small"
            disabled={busy || checking}
            aria-busy={busy || checking}
            onClick={() =>
              error ? notifications.retry() : void notifications.change(!on)
            }
          >
            {t(error ? "إعادة المحاولة" : on ? "إيقاف" : "تفعيل")}
          </button>
        )
      )}
    </div>
  );
}
